import type { OnInit } from '@angular/core';
import type { WebWidgetEntity } from '@web/types';

import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { DeviceMode } from '@common/types/layout.type';

import { INDENT_HEIGHT_BOUNDS } from '@sites/entity-core';
import type { IndentForm } from '@sites/entity-signal-form';

import { LayoutFacade } from '@web/store/layout/layout.facade';
import { WebWidgetSignalFormService } from '@web/core/services/web-widget-signal-form.service';

import { CommonElementsModule } from '@web/widgets/common/common-elements.module';

/**
 * Standalone, `input()`-based control panel for the `indent` (spacer) widget:
 * one height per breakpoint (`props.height.desktop` / `.mobile`) plus the
 * synchronisation toggle between them, written through the shared signal form.
 */
@Component({
    selector: 'web-indent-control',
    templateUrl: './indent-control.component.html',
    styleUrls: ['./indent-control.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule],
})
export class IndentControlComponent implements OnInit {
    public readonly widget = input.required<WebWidgetEntity>();
    public readonly isSpecific = input<boolean>(false);

    private readonly signalFormService = inject(WebWidgetSignalFormService);
    private readonly layoutFacade = inject(LayoutFacade);
    private readonly destroyRef = inject(DestroyRef);

    public readonly SIZE_MIN = INDENT_HEIGHT_BOUNDS.min;

    public readonly deviceMode = toSignal(this.layoutFacade.deviceMode$, { initialValue: DeviceMode.desktop });

    public form!: IndentForm;

    public readonly heightValue = computed<number>(() => {
        const mode = this.deviceMode();
        if (mode === DeviceMode.tablet) {
            return this.form.tabletHeightValue();
        }

        if (mode === DeviceMode.mobile) {
            return this.form.mobileHeightValue();
        }

        return this.form.height().value();
    });

    public readonly deviceHint = computed<string>(() => {
        const mode = this.deviceMode();

        if (mode === DeviceMode.mobile) {
            return 'mobile_device_hint';
        }

        if (mode === DeviceMode.tablet) {
            return 'tablet_device_hint';
        }

        return 'desktop_device_hint';
    });

    public ngOnInit(): void {
        this.form = this.signalFormService.getOrCreateScoped<IndentForm>(this.widget().id, this.destroyRef);
    }

    public setHeightValue(value: number | string): void {
        const height = Number(value);
        const mode = this.deviceMode();

        if (mode === DeviceMode.desktop || this.form.isSyncedWithDesktop()) {
            this.form.setHeight(height);
            return;
        }

        if (mode === DeviceMode.tablet) {
            this.form.setTabletHeight(height);
            return;
        }

        this.form.setMobileHeight(height);
    }

    /**
     * Turning synchronisation on copies the desktop height across, exactly as the
     * FormGroup control did — the flag alone would otherwise leave the two
     * devices showing different heights until the next desktop edit.
     */
    public toggleSyncWithDesktop(): void {
        this.form.transact(() => {
            const height = this.form.height().value();
            this.form.setTabletHeight(height);
            this.form.setMobileHeight(height);
            this.form.setSyncWithDesktop(!this.form.isSyncedWithDesktop());
        });
    }
}
